// src/components/orderentry/panels/HeaderFieldsPanel.jsx
import React from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

import GridBadge from '../components/GridBadge.jsx';

export default function HeaderFieldsPanel({
  disabled,
  company,
  masId,
  shipId,
  selectedAddressShipId,
  onShipIdChange,
  poNumber,
  setPoNumber,
  onPoBlur,
  confirmTo,
  setConfirmTo,
  confirmToOptions,
  confirmToLoading,
  phone,
  setPhone,
  phoneRef,
  onPhoneTab,
  receivingLine,
  setReceivingLine,
  confirmRequired,
  phoneRequired,
  poRef,
}) {
  // ---------------------------------------------------------------------------
  // BLOCK HFP.SHIPID.0 — TMS-only Ship ID override selector
  // ---------------------------------------------------------------------------
  const canOverrideShipId =
    String(company ?? '')
      .trim()
      .toLowerCase() === 'tms' && Boolean(String(masId ?? '').trim());

  const baseShipId = String(selectedAddressShipId ?? '')
    .trim()
    .toUpperCase();
  const shipIdOptions = baseShipId === 'P' ? ['P'] : [baseShipId, 'P'].filter(Boolean);

  return (
    <div style={{ position: 'relative' }}>
      <GridBadge n="2" />

      <Grid container spacing={2}>
        {/* PO Number — FAR LEFT */}
        <Grid size={{ xs: 12, sm: 2 }}>
          <TextField
            fullWidth
            label="PO Number"
            value={poNumber}
            onChange={(e) => setPoNumber(e.target.value)}
            onBlur={onPoBlur}
            inputRef={poRef}
            disabled={disabled}
            inputProps={{ tabIndex: 4 }}
          />
        </Grid>

        {/* MAS ID */}
        <Grid size={{ xs: 12, sm: 1.4 }}>
          <TextField fullWidth label="MAS ID (CustomerNo)" value={masId} disabled />
        </Grid>

        {/* Ship ID */}
        <Grid size={{ xs: 12, sm: 0.8 }}>
          <TextField
            fullWidth
            select={canOverrideShipId}
            label="Ship ID (ShipToCode)"
            value={
              canOverrideShipId
                ? String(shipId ?? '')
                    .trim()
                    .toUpperCase()
                : shipId
            }
            onChange={(e) => {
              if (typeof onShipIdChange === 'function') {
                onShipIdChange(e.target.value);
              }
            }}
            disabled={disabled || !canOverrideShipId}
            SelectProps={{ displayEmpty: true }}
          >
            {canOverrideShipId &&
              shipIdOptions.map((value) => (
                <MenuItem key={value} value={value}>
                  {value || "'' (default)"}
                </MenuItem>
              ))}
          </TextField>
        </Grid>

        {/* Receiving Line — WIDE */}
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            fullWidth
            label="Receiving Line"
            value={receivingLine}
            onChange={(e) => setReceivingLine(e.target.value)}
            disabled={disabled}
          />
        </Grid>

        {/* Confirm To */}
        <Grid size={{ xs: 12, sm: 2 }} sx={{ pr: 1 }}>
          <Autocomplete
            freeSolo
            disableClearable={false}
            options={Array.isArray(confirmToOptions) ? confirmToOptions : []}
            loading={Boolean(confirmToLoading)}
            value={String(confirmTo || '')}
            inputValue={String(confirmTo || '')}
            onInputChange={(e, newInput) => {
              if (typeof setConfirmTo === 'function') {
                setConfirmTo(newInput);
              }
            }}
            onChange={(e, newValue) => {
              const next = String(newValue || '');
              if (typeof setConfirmTo === 'function') {
                setConfirmTo(next);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Confirm To"
                disabled={disabled}
                error={!disabled && Boolean(confirmRequired)}
                helperText={!disabled && confirmRequired ? 'Required' : ''}
                inputProps={{
                  ...params.inputProps,
                  maxLength: 30,
                }}
              />
            )}
          />
        </Grid>
        {/* Phone — FAR RIGHT */}
        <Grid size={{ xs: 12, sm: 2 }}>
          <TextField
            fullWidth
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={onPhoneTab}
            inputRef={phoneRef}
            disabled={disabled}
            error={!disabled && Boolean(phoneRequired)}
            helperText={!disabled && phoneRequired ? 'Required' : ''}
            inputProps={{ maxLength: 25 }}
          />
        </Grid>
      </Grid>
    </div>
  );
}
